import { Action, createReducer, on } from '@ngrx/store';
import { EFileType } from 'src/app/conf/user-files';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { FileType } from 'src/app/model/file-type';
import { UserFile } from 'src/app/model/user-file';
import * as UserFileActions from './user-file.actions';
import { FinalUsersList } from 'src/app/model/final-users-list';
import { ExportInfo } from 'src/app/model/export';

export type State = {
	userFiles: UserFile[];
	userFilesContents: UserFile[];
	paging?: ItemsPagination;
	siteImages: UserFile[];
	fileTypes: FileType[];
	addEnd: boolean;
	loading: boolean;
	finalUsersLists: FinalUsersList[];
	finalUsersPaging?: ItemsPagination;
	fuListLoading: boolean;
	editing: boolean;
	edited: boolean;
	importing: boolean;
	imported: boolean;
	deletingList: boolean;
	deletedList: boolean;
	exportingFu: boolean;
	exportedFu: boolean;
	deletingFu: boolean;
	deletedFu: boolean;

	// Export Device
	exportingDevice: boolean;
	exportedDevice: boolean;
	updatingExportDevice: boolean;
	updatedExportDevice: boolean;
	deletingExportDevice: boolean;
	deletedExportDevice: boolean;
	exportDeviceList: ExportInfo[];
	exportDevicePaging?: ItemsPagination;
	exportDeviceListLoading: boolean;
	importingDevice: boolean;
	importedDevice: boolean;
	// Export Device

	// Export Device Mtma
	exportingMtmaDevice: boolean;
	exportedMtmaDevice: boolean;
	updatingMtmaExportDevice: boolean;
	updatedMtmaExportDevice: boolean;
	deletingMtmaExportDevice: boolean;
	deletedMtmaExportDevice: boolean;
	exportMtmaDeviceList: ExportInfo[];
	exportMtmaDevicePaging?: ItemsPagination;
	exportMtmaDeviceListLoading: boolean;
	importingMtmaDevice: boolean;
	importedMtmaDevice: boolean;
	// Export Device Mtma
};

export const initialState: State = {
	userFiles: [],
	userFilesContents: [],
	siteImages: [],
	fileTypes: [],
	addEnd: false,
	loading: false,
	finalUsersLists: [],
	fuListLoading: false,
	editing: false,
	edited: false,
	importing: false,
	imported: false,
	deletingList: false,
	deletedList: false,
	exportingFu: false,
	exportedFu: false,
	deletingFu: false,
	deletedFu: false,

	// Export Device
	exportingDevice: false,
	exportedDevice: false,
	updatingExportDevice: false,
	updatedExportDevice: false,
	deletingExportDevice: false,
	deletedExportDevice: false,
	exportDeviceListLoading: false,
	importingDevice: false,
	importedDevice: false,
	exportDeviceList: [],
	// Export Device

	// Export Device Mtma
	exportingMtmaDevice: false,
	exportedMtmaDevice: false,
	updatingMtmaExportDevice: false,
	updatedMtmaExportDevice: false,
	deletingMtmaExportDevice: false,
	deletedMtmaExportDevice: false,
	exportMtmaDeviceListLoading: false,
	importingMtmaDevice: false,
	importedMtmaDevice: false,
	exportMtmaDeviceList: [],
	// Export Device Mtma
};

const userFilesReducer = createReducer(
	initialState,
	on(UserFileActions.getUserFileTypesSuccess, (state, payload) => ({ ...state, fileTypes: payload.fileTypes })),
	on(UserFileActions.getUserFiles, (state) => ({ ...state, loading: true })),
	on(UserFileActions.getUserFilesSuccess, (state, payload) => ({
		...state,
		userFiles: payload.userFiles,
		paging: payload.paging,
		loading: false
	})),
	on(UserFileActions.getSiteImages, (state) => ({ ...state, loading: true })),
	on(UserFileActions.getSiteImagesSuccess, (state, payload) => ({
		...state,
		siteImages: payload.siteImages,
		loading: false
	})),
	on(UserFileActions.getUserFileContentSuccess, (state, payload) => {
		const userFileIndex = state.userFilesContents.findIndex((u) => u.Id === payload.userFile.Id);
		if (userFileIndex >= 0) {
			const uf = state.userFilesContents[userFileIndex];
			const uuf = {
				...uf,
				Content: payload.fileContent
			};
			const uufs = [...state.userFilesContents];
			uufs[userFileIndex] = uuf;
			return {
				...state,
				userFilesContents: uufs,
			};
		} else {
			const userFileContent = { ...payload.userFile, Content: payload.fileContent };
			return {
				...state,
				userFilesContents: [...state.userFilesContents, userFileContent]
			};
		}
	}),
	on(UserFileActions.addUserFile, (state) => ({ ...state, addEnd: false })),
	on(UserFileActions.addUserFileSuccess, (state, payload) => {
		const userFiles = [payload.userFile, ...state.userFiles];
		let siteImages = state.siteImages;
		if (payload.userFile.Type === EFileType.IMAGE) {
			siteImages = state.siteImages.filter(si => si.SiteId !== payload.userFile.SiteId);
			siteImages = [payload.userFile, ...siteImages];
		}
		return { ...state, userFiles, siteImages, addEnd: true };
	}),
	on(UserFileActions.addUserFileFailed, (state) => ({ ...state, addEnd: true })),
	on(UserFileActions.deleteUserFileSuccess, (state, payload) => ({
		...state,
		userFiles: state.userFiles.filter((uf) => uf.Id !== payload.userFile.Id),
	})),
	on(UserFileActions.clearFileContent, (state, payload) => ({
		...state,
		userFilesContents: state.userFilesContents.filter((uf) => uf.Id !== payload.userFile.Id)
	})),
	on(UserFileActions.resetFinalUsersListDialog, (state) => ({
		...state, editing: false, edited: false
	})),
	on(UserFileActions.exportFinalUsersList, (state) => ({ ...state, exportingFu: true, exportedFu: false })),
	on(UserFileActions.exportFinalUsersListSuccess, (state) => ({ ...state, exportingFu: false, exportedFu: true })),
	on(UserFileActions.exportFinalUsersListFailed, (state) => ({ ...state, exportingFu: false, exportedFu: false })),
	on(UserFileActions.getFinalUsersLists, (state) => ({ ...state, finalUsersLists: [], fuListLoading: true })),
	on(UserFileActions.getFinalUsersListsSuccess, (state, payload) => ({
		...state,
		finalUsersLists: payload.finalUsersLists,
		finalUsersPaging: payload.paging,
		fuListLoading: false
	})),
	on(UserFileActions.deleteFinalUsersList, (state) => ({ ...state, deletingList: true, deletedList: false })),
	on(UserFileActions.deleteFinalUsersListSuccess, (state, payload) => ({
		...state,
		finalUsersLists: state.finalUsersLists.filter((uf) => uf.Id !== payload.finalUsersLists.Id),
		deletingList: false, deletedList: true
	})),
	on(UserFileActions.deleteFinalUsersListFailed, (state) => ({ ...state, deletingList: false, deletedList: false })),
	on(UserFileActions.editFinalUsersList, (state) => ({ ...state, editing: true, edited: false })),
	on(UserFileActions.editFinalUsersListSuccess, (state) => ({ ...state, editing: false, edited: true })),
	on(UserFileActions.editFinalUsersListFailed, (state) => ({ ...state, editing: false, edited: false })),
	on(UserFileActions.importFinalUsersList, (state) => ({ ...state, importing: true, imported: false })),
	on(UserFileActions.importFinalUsersListSuccess, (state) => ({ ...state, importing: false, imported: true })),
	on(UserFileActions.importFinalUsersListFailed, (state) => ({ ...state, importing: false, imported: false })),
	on(UserFileActions.deleteFinalUsers, (state) => ({ ...state, deletingFu: true, deletedFu: false })),
	on(UserFileActions.deleteFinalUsersSuccess, (state) => ({ ...state, deletingFu: false, deletedFu: true })),
	on(UserFileActions.deleteFinalUsersFailed, (state) => ({ ...state, deletingFu: false, deletedFu: false })),
	on(UserFileActions.resetFinalUsersList, (state) => ({
		...state, importing: false, imported: false, exportingFu: false, exportedFu: false, deletingFu: false, deletedFu: false
	})),

	// ---------- Export device
	on(UserFileActions.resetExportDeviceDialog, (state) => ({ ...state, exportingDevice: false, exportedDevice: false, updatingExportDevice: false, updatedExportDevice: false })),
	on(UserFileActions.exportDevice, (state) => ({ ...state, exportingDevice: true, exportedDevice: false })),
	on(UserFileActions.exportDeviceSuccess, (state) => ({ ...state, exportingDevice: false, exportedDevice: true })),
	on(UserFileActions.exportDeviceFailed, (state) => ({ ...state, exportingDevice: false, exportedDevice: false })),
	on(UserFileActions.updateExportDevice, (state) => ({ ...state, updatingExportDevice: true, updatedExportDevice: false })),
	on(UserFileActions.updateExportDeviceSuccess, (state) => ({ ...state, updatingExportDevice: false, updatedExportDevice: true })),
	on(UserFileActions.updateExportDeviceFailed, (state) => ({ ...state, updatingExportDevice: false, updatedExportDevice: false })),
	on(UserFileActions.deleteExportDevice, (state) => ({ ...state, deletingExportDevice: true, deletedExportDevice: false })),
	on(UserFileActions.deleteExportDeviceSuccess, (state) => ({ ...state, deletingExportDevice: false, deletedExportDevice: true })),
	on(UserFileActions.deleteExportDeviceFailed, (state) => ({ ...state, deletingExportDevice: false, deletedExportDevice: false })),
	on(UserFileActions.getExportDeviceList, (state) => ({ ...state, exportDeviceList: [], exportDeviceListLoading: true })),
	on(UserFileActions.getExportDeviceListSuccess, (state, payload) => ({
		...state,
		exportDeviceList: payload.exportLists,
		exportDevicePaging: payload.paging,
		exportDeviceListLoading: false,
	})),
	on(UserFileActions.getExportDeviceListFailed, (state) => ({ ...state, exportDeviceList: [], exportDeviceListLoading: false, })),
	on(UserFileActions.resetImportDeviceDialog, (state) => ({ ...state, importingDevice: false, importedDevice: false })),
	on(UserFileActions.importDevice, (state) => ({ ...state, importingDevice: true, importedDevice: false })),
	on(UserFileActions.importDeviceSuccess, (state) => ({ ...state, importingDevice: false, importedDevice: true })),
	on(UserFileActions.importDeviceFailed, (state) => ({ ...state, importingDevice: false, importedDevice: false })),
	// ---------- Export device

	// ---------- Export device Mtma
	on(UserFileActions.resetMtmaExportDeviceDialog, (state) => ({ ...state, exportingMtmaDevice: false, exportedMtmaDevice: false, updatingMtmaExportDevice: false, updatedMtmaExportDevice: false })),
	on(UserFileActions.exportMtmaDevice, (state) => ({ ...state, exportingMtmaDevice: true, exportedMtmaDevice: false })),
	on(UserFileActions.exportMtmaDeviceSuccess, (state) => ({ ...state, exportingMtmaDevice: false, exportedMtmaDevice: true })),
	on(UserFileActions.exportMtmaDeviceFailed, (state) => ({ ...state, exportingMtmaDevice: false, exportedMtmaDevice: false })),
	on(UserFileActions.updateMtmaExportDevice, (state) => ({ ...state, updatingMtmaExportDevice: true, updatedMtmaExportDevice: false })),
	on(UserFileActions.updateMtmaExportDeviceSuccess, (state) => ({ ...state, updatingMtmaExportDevice: false, updatedMtmaExportDevice: true })),
	on(UserFileActions.updateMtmaExportDeviceFailed, (state) => ({ ...state, updatingMtmaExportDevice: false, updatedMtmaExportDevice: false })),
	on(UserFileActions.deleteMtmaExportDevice, (state) => ({ ...state, deletingMtmaExportDevice: true, deletedMtmaExportDevice: false })),
	on(UserFileActions.deleteMtmaExportDeviceSuccess, (state) => ({ ...state, deletingMtmaExportDevice: false, deletedMtmaExportDevice: true })),
	on(UserFileActions.deleteMtmaExportDeviceFailed, (state) => ({ ...state, deletingMtmaExportDevice: false, deletedMtmaExportDevice: false })),
	on(UserFileActions.getMtmaExportDeviceList, (state) => ({ ...state, exportMtmaDeviceList: [], exportMtmaDeviceListLoading: true })),
	on(UserFileActions.getMtmaExportDeviceListSuccess, (state, payload) => ({
		...state,
		exportMtmaDeviceList: payload.exportLists,
		exportMtmaDevicePaging: payload.paging,
		exportMtmaDeviceListLoading: false,
	})),
	on(UserFileActions.getMtmaExportDeviceListFailed, (state) => ({ ...state, exportMtmaDeviceList: [], exportMtmaDeviceListLoading: false, })),
	on(UserFileActions.resetMtmaImportDeviceDialog, (state) => ({ ...state, importingMtmaDevice: false, importedMtmaDevice: false })),
	on(UserFileActions.importMtmaDevice, (state) => ({ ...state, importingMtmaDevice: true, importedMtmaDevice: false })),
	on(UserFileActions.importMtmaDeviceSuccess, (state) => ({ ...state, importingMtmaDevice: false, importedMtmaDevice: true })),
	on(UserFileActions.importMtmaDeviceFailed, (state) => ({ ...state, importingMtmaDevice: false, importedMtmaDevice: false })),
	// ---------- Export device Mtma

);

export const reducer = (state: State | undefined, action: Action): State => userFilesReducer(state, action);
